import { Button, ButtonGroup, Intent } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, InlineSearchCriteriaPanel, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import {
  DashboardApi,
  DashboardSearch,
  DashboardTaskGridDto,
  ERequestType,
  RequestApi,
  RequestGridDto,
} from "../../../api";
import { CustomDataTable, ExportButton } from "../../../components";
import { useGlobalData } from "../../../contexts";
import { ERoutes } from "../../../ERoutes";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { getRouteStr } from "../../../utils/RequestRouteUtil";
import { getSearchRouteStr } from "../../../utils/RequestSearchRouteUtil";
import { MyTaskSubCard } from "./MyTaskSubCard";

export interface IMyAssignedTasksListProps {}

export const MyAssignedTasksList: React.FunctionComponent<IMyAssignedTasksListProps> = (props) => {
  const { t } = useTl();
  const api = useApiService(DashboardApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5, 10, 15],
    pageSize: 10,
    sortKeys: { requestNumber: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DashboardSearch) => {
      return api.baseSearch(
        DashboardSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
          assigned: true,
        })
      );
    },
    [api, currentLanguage.languageId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const openRequest = React.useCallback(
    async (requestId: number, requestType: ERequestType) => {
      const subRequestId = await requestApi.getSubRequestTypeId(requestId, requestType);
      const route = await getRouteStr(requestType, subRequestId, "view");
      window.open(`#${route}`, "_blank");
    },
    [requestApi]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "_toggle",
        autoFitContent: true,
        render: (item: DashboardTaskGridDto, _, { subPanelOpened, toggleSubPanel }) => (
          <Button
            icon={subPanelOpened ? "minus" : "plus"}
            onClick={() => toggleSubPanel()}
            disabled={item?.subTasks?.length === 0}
          />
        ),
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
        render: (row: DashboardTaskGridDto) => {
          return (
            <Button
              intent={Intent.PRIMARY}
              minimal={true}
              onClick={() => openRequest(row.requestId, row.requestTypeCode)}
              text={row.requestNumber}
            />
          );
        },
      },
      {
        header: () => t(ETLCodes.RequestType),
        fieldName: "requestTypeCode",
        render: (row: RequestGridDto) => (
          <Link target="_blank" to={getSearchRouteStr(row.requestTypeCode)}>
            {row.requestType}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: RequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
    ],
    [openRequest, t]
  );

  const searchCriteriasFunction = React.useCallback(() => api.getSearchCriterias(true), [api]);

  return (
    <FieldSet
      title={t(ETLCodes.MyTasks)}
      rightElement={
        <ButtonGroup>
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={DashboardSearch} />
        </ButtonGroup>
      }
    >
      <InlineSearchCriteriaPanel
        searchFunc={searchCriteriasFunction}
        onSearch={search}
        showAllCriteriaRemovalButton={true}
        allCriteriaRemovalMode="RemoveAll"
        triggerSearchOnAllCriteriaRemoved={true}
      />

      <FieldSet>
        <CustomDataTable
          tableState={tableState}
          loading={loading}
          columns={columns}
          keyFieldName="requestId"
          renderSubComponent={(item: DashboardTaskGridDto) => <MyTaskSubCard data={item.subTasks} />}
        />
      </FieldSet>
    </FieldSet>
  );
};
